import { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { I18n, Translate } from 'common'
import { breakpoints } from '../../theme/breakpoints'

interface ThumbnailProps {
  urlOrBase64?: string | null
  alt?: string
  width: number
  height?: number
  placeholder?: boolean
}

// Helper function to determine max-height
const getMaxHeight = ({ alt, height }: { alt?: string; height?: number }) => {
  if (alt === 'Brand') return '80px'
  if (height) return `${height}px`
  return '100px'
}

export const Thumbnail = ({
  urlOrBase64,
  width,
  height,
  alt = 'Thumbnail',
  placeholder = false,
}: ThumbnailProps): ReactElement => {
  const [imageLoadFailed, setImageLoadFailed] = useState<boolean>(false)

  useEffect(() => {
    setImageLoadFailed(false)
  }, [urlOrBase64])

  const isBase64 = !urlOrBase64?.startsWith('http')
  const src = isBase64 ? `data:image/jpeg;base64,${urlOrBase64}` : urlOrBase64
  const translatedAlt = Translate(alt || '')

  const Placeholder = () => (
    <NoImage width={width} height={height || 100}>
      <I18n name="noImage" />
    </NoImage>
  )

  if (imageLoadFailed || (!urlOrBase64 && placeholder)) {
    return <Placeholder />
  }
  return (
    <ThumbnailWrapper width={width} height={height} alt={translatedAlt}>
      <StyledImg
        alt={translatedAlt}
        src={src}
        width={width}
        height={height}
        onError={() => setImageLoadFailed(true)}
      />
    </ThumbnailWrapper>
  )
}

interface ImgSize {
  width: number
  height: number
  alt?: string
}

const ThumbnailWrapper = styled.div<ImgSize>`
  width: ${(p) => p.width}px;
  height: ${(p) => p.height && `${p.height}px`};
  display: flex;
  justify-content: center;
  align-items: flex-start;
  max-height: ${(p) => (p.alt === 'Brand' ? '80px' : '100px')};
  @media (max-width: ${breakpoints.mobileLarge}px) {
    width: ${(p) => p.width / 2}px;
    height: ${(p) => p.height / 2}px;
  }
`

const StyledImg = styled.img<ImgSize>`
  height: ${(p) => p.height && 'auto'};
  width: auto;
  max-width: ${(p) => p.width}px;
  max-height: ${(p) => getMaxHeight(p)};
  pointer-events: none;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    width: ${(p) => p.width / 2}px;
    height: ${(p) => p.height / 2}px;
    max-width: ${(p) => p.width / 2}px;
    max-height: ${(p) => p.height / 2}px;
    object-fit: contain;
  }
  @media print {
    width: 50px;
    height: 50px;
  }
`

const NoImage = styled.div<ImgSize>`
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
  background: #eee;
  color: #999;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: clamp(8px, ${(p) => p.height * 0.3}px, 14px);
  line-height: 1.1;
  padding: 0 8px;
  overflow: hidden;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    width: ${(p) => p.width / 2}px;
    height: ${(p) => p.height / 2}px;
    font-size: 10px;
  }
  @media print {
    width: 50px;
    height: 50px;
    font-size: 10px;
  }
`
